import { useEffect, useMemo } from 'react';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { ManualTemplatesTypes, ManualTemplatesQueries } from '@frontend/api-manual-templates';
import { useScopedAppFlagStore } from '@frontend/scope';
import { ReviewAppointmentLinkPatient } from '../../../../../../components/ReviewAppointmentLinkPatient';
import { ReviewAppointmentRequestFormV3 } from '../../../../../../components/ReviewAppointmentRequest/ReviewAppointmentRequestV3';
import { normalizeString } from '../../../../utils';
import { useScheduleRequestModalContext } from '../Context/ScheduleRequestModalContext';
import { useScheduleRequestModalFormContext } from '../Context/ScheduleRequestModalFormContext';
import { ReducedAppointmentType, ReducedOperatory, ReducedPractitioner } from '../hooks/useReviewRequest';
import { getDistinctEntities } from '../utils';
import { SourceTenantListItem, WorkstationDropdownList } from './types';
import { useGetAvailabilityData } from './use-get-availability-data';

const { ManualTemplateTypes } = ManualTemplatesTypes;

type ScheduleRequestModalFormContainerV3Props = {
  appointmentWritebackSourceTenants: SourceTenantListItem[];
  patientWritebackSourceTenants: SourceTenantListItem[];
  isLoadingSourceTenants: boolean;
};

export const ScheduleRequestModalFormContainerV3 = ({
  appointmentWritebackSourceTenants,
  patientWritebackSourceTenants,
  isLoadingSourceTenants,
}: ScheduleRequestModalFormContainerV3Props) => {
  const { selectedScheduleRequest } = useScheduleRequestModalContext();
  const { getFeatureFlagValue } = useScopedAppFlagStore();

  const {
    personSelectorDialogProps,
    manageScheduleRequestFormDetails,
    manageScheduleRequestModalDetails,
    selectedPerson,
    personSelectorProps,
    setTemplate,
  } = useScheduleRequestModalFormContext();

  const {
    scheduleRequestApprovalForm: { values, seedValues, getFieldProps },
    customDateField,
    customStartTimeField,
    customEndTimeField,
  } = manageScheduleRequestFormDetails;

  const {
    access,
    isLoading: isLoadingModalDetails,
    isSyncAppDetailsLoading,
    appointmentTypes: appointmentTypesV3,
    providers: providersV3,
    workstations: workstationsV3,
    tags,
  } = manageScheduleRequestModalDetails;

  // Type cast to V3-specific types
  const providers = providersV3 as ReducedPractitioner[];
  const appointmentTypes = appointmentTypesV3 as ReducedAppointmentType[];
  const workstations = workstationsV3 as ReducedOperatory[];

  const { data: scheduleRequestConfirmedTemplate } = ManualTemplatesQueries.useGetManualTemplateByType({
    templateType: ManualTemplateTypes.onlineSchedulingConfirmation,
    locationId: selectedScheduleRequest.locationId ?? '',
  });

  const { isIntegratedOffice, data: integrations } = DataSourcesQueries.useGetAccessibleIntegrations();

  const isRealTimeAvailabilityEnabled = isIntegratedOffice && getFeatureFlagValue('schedule-real-time-availability');

  const { availabilityQuery } = useGetAvailabilityData({
    locationId: selectedScheduleRequest.locationId,
    appointmentTypes: appointmentTypes,
    providers: providers,
    isRealTimeAvailabilityEnabled,
    customDateField,
    customStartTimeField,
    customEndTimeField,
    values,
  });

  /*
    Filtering of providers and operatories will need an update later. For now, showing all the providers and operatories for the location
    `provider.sourceId` and `operatory.sourceId` are the sourceTenantIds but named as sourceId during transformation to make it compatible with v2 data
  */
  const { filteredProviderList, filteredAppointmentTypeList, filteredWorkstationList, allValidWorkstationList } =
    useMemo(() => {
      // Providers
      const distinctProviders = getDistinctEntities(providers);
      const filteredProviders = isIntegratedOffice
        ? distinctProviders?.filter((provider) => provider.sourceId)
        : distinctProviders?.filter((provider) => {
            const isSelectedProvider =
              provider.id === selectedScheduleRequest.practitionerId ||
              normalizeString(provider.publicDisplayName) ===
                normalizeString(selectedScheduleRequest?.requestedOpenings?.[0]?.assets?.providers?.[0]);

            return isSelectedProvider || (!provider.hidden && provider.isActive);
          });

      // Appointment Types
      const hasServiceType = appointmentTypes.some((apptType) => 'bookableAppointmentTypeId' in apptType);
      const distinctAppointmentTypes = hasServiceType ? appointmentTypes : getDistinctEntities(appointmentTypes);
      const filteredAppointmentTypes = isIntegratedOffice
        ? distinctAppointmentTypes
        : distinctAppointmentTypes.filter((apptType) => {
            const isSelectedApptType =
              apptType.id === selectedScheduleRequest.appointmentTypeId ||
              normalizeString(apptType.name) === normalizeString(selectedScheduleRequest?.appointmentType);
            return isSelectedApptType || ('active' in apptType && apptType.active);
          });

      // Workstations
      const availableTimes = availabilityQuery.data?.availableSlots ?? [];
      const distinctWorkstations = getDistinctEntities(workstations);
      const allValidWorkstations = isIntegratedOffice
        ? distinctWorkstations?.filter((workstation) => workstation.workstationName && workstation.sourceId)
        : distinctWorkstations;

      // Extract unique operatory IDs from available times
      // TODO: remove 'any' type when schema is updated by backend
      const availableOperatoryIds = new Set(availableTimes.flatMap((timeSlot: any) => timeSlot.operatoryIds || []));

      const availableWorkstations =
        allValidWorkstations?.filter((workstation) => availableOperatoryIds.has(workstation.externalId || '')) || [];

      const filteredWorkstations = isRealTimeAvailabilityEnabled
        ? { available: availableWorkstations, all: allValidWorkstations }
        : { available: [], all: allValidWorkstations };

      return {
        filteredProviderList: filteredProviders,
        filteredAppointmentTypeList: filteredAppointmentTypes,
        filteredWorkstationList: filteredWorkstations,
        allValidWorkstationList: allValidWorkstations,
      };
    }, [
      isIntegratedOffice,
      providers,
      appointmentTypes,
      availabilityQuery.data?.availableSlots,
      workstations,
      isRealTimeAvailabilityEnabled,
      selectedScheduleRequest.practitionerId,
      selectedScheduleRequest?.requestedOpenings,
      selectedScheduleRequest.appointmentTypeId,
      selectedScheduleRequest?.appointmentType,
    ]);

  // Update source form field value when there is only one source location
  useEffect(() => {
    let source = '';

    if (isIntegratedOffice && appointmentWritebackSourceTenants.length === 1) {
      source = appointmentWritebackSourceTenants[0].sourceTenantId || '';
    } else if (!isIntegratedOffice && integrations?.length === 1) {
      source = integrations?.[0]?.SourceID;
    }

    if (source) {
      seedValues({
        ...values,
        source,
      });
    }
  }, [
    isIntegratedOffice,
    appointmentWritebackSourceTenants.length,
    integrations?.length,
    appointmentWritebackSourceTenants,
  ]);

  // Handle workstation field
  useEffect(() => {
    // Auto-select workstation if there's only one total workstation across both lists
    if (allValidWorkstationList.length === 1) {
      seedValues({
        ...values,
        workstation: allValidWorkstationList[0]?.id,
      });
    }
  }, [allValidWorkstationList, filteredWorkstationList.available, isRealTimeAvailabilityEnabled]);

  // Reset workstation on changing other fields when multiple workstations are available
  useEffect(() => {
    if (allValidWorkstationList.length > 1 && values.workstation) {
      // If the currently selected workstation is not available in the filtered list, reset it.
      // For real-time availability sources, check only in the available list
      const workstationList = isRealTimeAvailabilityEnabled
        ? [...filteredWorkstationList.available]
        : allValidWorkstationList;

      const isWorkstationAvailable = workstationList.some(({ id }) => id === values.workstation);

      if (!isWorkstationAvailable) {
        seedValues({
          ...values,
          workstation: '',
        });
      }
    }
  }, [
    values.dateAndTime,
    values.provider,
    values.appointmentType,
    customDateField,
    customStartTimeField,
    customEndTimeField,
  ]);

  // Seed form values using selectedScheduleRequest
  useEffect(() => {
    let providerId = selectedScheduleRequest.practitionerId;
    const bookableAppointmentTypeId =
      'bookableAppointmentTypeId' in selectedScheduleRequest
        ? typeof selectedScheduleRequest.bookableAppointmentTypeId === 'string'
          ? selectedScheduleRequest.bookableAppointmentTypeId
          : undefined
        : undefined;
    let appointmentTypeId = bookableAppointmentTypeId || selectedScheduleRequest.appointmentTypeId;

    // Find provider and appointment type IDs based on requested data for v2 appointment request when using combined requests
    if (selectedScheduleRequest.linkHash === 'v2') {
      if (!providerId) {
        providerId = filteredProviderList.find(
          (provider) =>
            normalizeString(provider.publicDisplayName) ===
            normalizeString(selectedScheduleRequest?.requestedOpenings?.[0]?.assets?.providers?.[0])
        )?.id;
      }
      if (!appointmentTypeId) {
        appointmentTypeId = appointmentTypes.find(
          (apptType) => normalizeString(apptType.name) === normalizeString(selectedScheduleRequest?.appointmentType)
        )?.id;
      }
    }

    if (selectedScheduleRequest) {
      seedValues({
        ...values,
        provider: providerId,
        appointmentType: appointmentTypeId,
      });
    }
  }, [selectedScheduleRequest, filteredProviderList.length, appointmentTypes.length]);

  const isLoading = isLoadingModalDetails || isSyncAppDetailsLoading || isLoadingSourceTenants;
  const showSourceLocationDropdown = isIntegratedOffice && appointmentWritebackSourceTenants.length > 1;
  const showWorkstationDropdown = allValidWorkstationList.length > 1;

  // Set configured template
  useEffect(() => {
    setTemplate(scheduleRequestConfirmedTemplate?.Template ?? '');
  }, [scheduleRequestConfirmedTemplate?.Template, setTemplate]);

  return (
    <>
      <ReviewAppointmentRequestFormV3
        appointmentTypes={filteredAppointmentTypeList}
        isLoading={isLoading}
        dateField={customDateField}
        startTimeField={customStartTimeField}
        endTimeField={customEndTimeField}
        getFieldProps={getFieldProps}
        providerList={filteredProviderList as ReducedPractitioner[]}
        selectedScheduleRequest={selectedScheduleRequest}
        showSourceLocationDropdown={showSourceLocationDropdown}
        showWorkstationDropdown={showWorkstationDropdown}
        appointmentWritebackSourceTenants={appointmentWritebackSourceTenants}
        values={values}
        workstationList={filteredWorkstationList as WorkstationDropdownList<ReducedOperatory>}
        isLoadingAvailableWorkstations={availabilityQuery.isLoading}
        showGroupedWorkstations={isRealTimeAvailabilityEnabled}
        template={scheduleRequestConfirmedTemplate?.Template ?? ''}
        templateTags={tags}
        onTemplateChange={(template: string) => setTemplate(template)}
      />
      <ReviewAppointmentLinkPatient
        access={access}
        isIntegratedOffice={isIntegratedOffice}
        personSelectorProps={personSelectorProps}
        personSelectorDialogProps={personSelectorDialogProps}
        selectedPerson={selectedPerson}
        selectedScheduleRequest={selectedScheduleRequest}
        patientWritebackSourceTenants={patientWritebackSourceTenants}
      />
    </>
  );
};
